import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  SearchInput,
  SearchInputProps,
} from '@unity/cloud-ui';
import { SearchBox, SearchBoxState, Suggestion } from '@coveo/headless';
import { MagnifyingGlassIcon, XCircleIcon } from '@phosphor-icons/react';
import { useTheme } from '@unity/cloud-ui/styles';
import { useSearchParams } from 'next/navigation';
import { useRouter } from '@/i18n/routing';
import { SearchSuggestions } from '../search-suggestions';
import { BaseSearchInputShortcut } from './base-search-input-shortcut';

export const SEARCH_DEBOUNCE_DELAY_MS = 250;

export const keyCodes = {
  ENTER: 13,
  ESCAPE: 27,
};

export const BaseSearchInput = (
  props: {
    renderSearchButton?: boolean;
    navigateOnSearch?: boolean;
    smallerSuggestions?: boolean;
    searchBoxState?: SearchBoxState;
    overrideSlotProps?: boolean;
    hideSuggestions?: boolean;
    searchFieldRef?: React.RefObject<HTMLInputElement | null>;
    methods: Omit<SearchBox, 'state' | 'subscribe'> | null;
  } & SearchInputProps,
) => {
  const {
    renderSearchButton,
    navigateOnSearch,
    overrideSlotProps,
    searchBoxState,
    smallerSuggestions,
    hideSuggestions,
    searchFieldRef,
    methods,
    ...rest
  } = props;
  const { clear, submit, selectSuggestion, updateText } = methods || {};
  const t = useTranslations();
  const router = useRouter();
  const theme = useTheme();
  const searchParams = useSearchParams();
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  useEffect(() => {
    setIsLoading(false);
  }, [searchParams]);

  // Controlled input state for query text
  const [inputText, setInputText] = useState(searchBoxState?.value || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [inputInFocus, setInputInFocus] = useState(false);
  const hasPendingDebounceRef = useRef(false);

  const shouldSuggestionBeOpen = useCallback(() => {
    return (
      searchBoxState?.value !== '' &&
      searchBoxState?.suggestions &&
      searchBoxState?.suggestions.length > 0 &&
      inputInFocus
    );
  }, [searchBoxState?.value, searchBoxState?.suggestions, inputInFocus]);

  const clearIfEmpty = useCallback(() => {
    if (inputText === '' && searchBoxState?.value.length === 0) {
      clear?.();
    }
  }, [clear, inputText, searchBoxState?.value.length]);

  useEffect(() => {
    setInputText(searchBoxState?.value || '');
  }, [searchBoxState?.value]);

  const handleUpdateSearchBoxText = useCallback(
    (text: string) => {
      updateText?.(text);
      hasPendingDebounceRef.current = false;
      return text;
    },
    [updateText],
  );

  useEffect(() => {
    setIsSuggestionOpen(shouldSuggestionBeOpen() || false);
    clearIfEmpty();
  }, [searchBoxState?.suggestions, inputText, clearIfEmpty, shouldSuggestionBeOpen]);

  const debouncedAutocomplete = useMemo(() => {
    hasPendingDebounceRef.current = true;
    return debounce(handleUpdateSearchBoxText, SEARCH_DEBOUNCE_DELAY_MS, {
      leading: true,
      trailing: true,
      maxWait: SEARCH_DEBOUNCE_DELAY_MS,
    });
  }, [handleUpdateSearchBoxText]);

  const handleUpdateURLParam = (newQuery: string) => {
    if (navigateOnSearch) {
      setIsLoading?.(true);
      router.push(`/search?query=${newQuery}`);
    } else {
      window.history.replaceState(null, '', `?query=${newQuery}`);
    }
  };

  const handleSearchInputOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
    debouncedAutocomplete(event.target.value);
  };

  const clearCoveoSuggestions = () => {
    setIsSuggestionOpen(false);
  };

  const handleSubmitSearchQuery = () => {
    if (inputText === '') {
      return;
    }
    setIsSuggestionOpen(false);
    const text = hasPendingDebounceRef.current && !navigateOnSearch ? debouncedAutocomplete.flush() : inputText;
    submit?.();
    handleUpdateURLParam(text);
    const scrollContainer = document.querySelector('[data-target="search-results-scroll-container"]');
    scrollContainer?.scrollTo({ top: 0 });
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === keyCodes.ESCAPE) {
      clearCoveoSuggestions();
    } else if (event.keyCode === keyCodes.ENTER && inputText && highlightedIndex < 0) {
      event.stopPropagation();
      handleSubmitSearchQuery();
    }
  };

  const handleOnFocus = () => {
    setIsSuggestionOpen(true);
    setInputInFocus(true);
  };

  const handleOnBlur = () => {
    setTimeout(() => {
      clearCoveoSuggestions();
      setInputInFocus(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const handleSuggestionsClick = (suggestion: Suggestion) => {
    setInputText(suggestion.rawValue);
    setIsSuggestionOpen(false);
    selectSuggestion?.(suggestion.rawValue);
    handleUpdateURLParam(suggestion.rawValue);
    const scrollContainer = document.querySelector('[data-target="search-results-scroll-container"]');
    scrollContainer?.scrollTo({ top: 0 });
  };

  const handleClearInput = () => {
    setInputText('');
    searchFieldRef?.current?.focus();
    clear?.();
  };

  return (
    <>
      <Box sx={{ flex: 1, position: 'relative' }}>
        <SearchInput
          {...rest}
          aria-label={t('search:box:aria-label')}
          role="searchbox"
          autoComplete="off"
          value={inputText}
          onChange={handleSearchInputOnChange}
          onClear={handleClearInput}
          onKeyDown={handleOnKeyDown}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          inputProps={{
            'aria-label': t('search:input:aria-label'),
            tabIndex: 0,
          }}
          slotProps={
            overrideSlotProps
              ? {
                  input: {
                    startAdornment: isLoading ? (
                      <InputAdornment position="start" color={theme.vars.palette.background.paperElevation7}>
                        <CircularProgress size={18} color={'inherit'} />
                      </InputAdornment>
                    ) : (
                      <InputAdornment position="start" color={theme.vars.palette.background.paperElevation7}>
                        <MagnifyingGlassIcon
                          size={18}
                          aria-hidden="true"
                          focusable="false"
                          color={theme.vars.palette.background.paperElevation7}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end" sx={{ width: '2.5rem', justifyContent: 'flex-end' }}>
                        {inputText ? (
                          <IconButton size="small" aria-label={t('search:clear:aria-label')} onClick={handleClearInput}>
                            <XCircleIcon weight="fill" size={18} />
                          </IconButton>
                        ) : (
                          <BaseSearchInputShortcut />
                        )}
                      </InputAdornment>
                    ),
                  },
                }
              : {
                  input: {
                    sx: {
                      '> fieldset':
                        isSuggestionOpen && searchBoxState?.suggestions && searchBoxState?.suggestions.length > 0
                          ? {
                              borderBottom: 'none',
                              borderRadius: `0.5rem 0.5rem 0px 0px`,
                            }
                          : {},
                    },
                  },
                }
          }
        />
        {isSuggestionOpen && !hideSuggestions && (
          <SearchSuggestions
            suggestions={searchBoxState?.suggestions || []}
            onSuggestionsClick={handleSuggestionsClick}
            smallerVariant={smallerSuggestions}
            highlightedIndex={highlightedIndex}
            setHighlightedIndex={setHighlightedIndex}
          />
        )}
      </Box>
      {renderSearchButton && (
        <Button variant="contained" onClick={handleSubmitSearchQuery}>
          {t('search:search')}
        </Button>
      )}
    </>
  );
};
